import { escapeHtml } from '../../../engine/core/validation.js';
import { t } from '../../../shared/i18n/index.js';

const primary = () => /mac|iphone|ipad/i.test(navigator.userAgentData?.platform || navigator.platform || '') ? '⌘' : 'Ctrl';
const withPrimary = key => `${primary()}+${key}`;

/** Only advertise shortcuts implemented in the active view. */
export function contextShortcut(action, view) {
  if (view === 'markdown') return '';
  if (['copy', 'cut', 'paste'].includes(action)) return withPrimary({ copy: 'C', cut: 'X', paste: 'V' }[action]);
  if (action === 'daily' && ['bubble', 'mindmap', 'gantt', 'kanban'].includes(view)) return 'W';
  if (action === 'edit' && ['bubble', 'mindmap', 'gantt'].includes(view)) return 'E';
  if (['bubble', 'mindmap'].includes(view)) return { add: 'A / Tab', delete: 'Del' }[action] || '';
  return '';
}

function sections() {
  const drag = t('app.shortcuts.key.drag');
  const click = t('app.shortcuts.key.click');
  const row = (key, keys, hint = false) => [t(`app.shortcuts.${key}`), keys, hint ? t(`app.shortcuts.${key}.hint`) : undefined];
  return [
    [t('app.shortcuts.find'), [
      [t('html.shortcuts.title'), ['?']],
      row('find.search', [withPrimary('F')]),
      row('find.next', [withPrimary('G')]),
      row('find.previous', [withPrimary('Shift+G')]),
      row('find.up', ['U', 'Esc']),
      row('find.structure', ['[']),
    ]],
    [t('app.shortcuts.daily'), [
      row('daily.notes', ['N']),
      row('daily.routines', ['R']),
      row('daily.task', ['W'], true),
      row('daily.routine', ['Shift+R'], true),
    ]],
    [t('app.shortcuts.edit'), [
      row('edit.child', ['A', 'Tab'], true),
      row('edit.rename', ['Enter', 'F2'], true),
      row('edit.properties', ['E'], true),
      row('edit.tags', ['T'], true),
      row('edit.fold', ['C'], true),
      row('edit.delete', ['Del', 'Backspace'], true),
    ]],
    [t('app.shortcuts.clipboard'), [
      row('clipboard.copy', [withPrimary('C')]),
      row('clipboard.cut', [withPrimary('X')]),
      row('clipboard.paste', [withPrimary('V')]),
      [t('common.undo'), [withPrimary('Z')]],
      [t('common.redo'), [withPrimary('Shift+Z'), withPrimary('Y')]],
    ]],
    [t('app.shortcuts.move'), [
      row('move.zoom', ['+', '−'], true),
      row('move.reparent', [`Ctrl+${drag}`], true),
      row('move.cancelDrag', ['Esc'], true),
    ]],
    [t('app.shortcuts.select'), [
      row('select.rectangle', [`Shift+${drag}`], true),
      row('select.toggle', [withPrimary(click)], true),
      row('select.range', [`Shift+${click}`], true),
      row('select.tag', ['T'], true),
      row('select.move', [`Ctrl+${drag}`], true),
      row('select.leave', ['Esc'], true),
    ]],
  ];
}

let initialized = false;
export function initShortcutHelp({ show, close }) {
  if (initialized) return;
  initialized = true;
  const modal = document.getElementById('shortcuts-modal');
  const input = document.getElementById('shortcuts-search');
  const content = document.getElementById('shortcuts-groups');
  const empty = document.getElementById('shortcuts-empty');
  const filter = () => {
    const words = input.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
    let count = 0;
    for (const section of content.querySelectorAll('.shortcuts-section')) {
      const heading = section.querySelector('h3').textContent;
      let matches = 0;
      for (const row of section.querySelectorAll('.shortcut-row')) {
        const text = `${heading} ${row.textContent}`.toLowerCase();
        row.hidden = !words.every(word => text.includes(word));
        if (!row.hidden) matches++;
      }
      section.hidden = !matches;
      count += matches;
    }
    empty.hidden = count > 0;
  };
  const open = () => {
    if (modal.classList.contains('active')) return;
    input.value = '';
    content.innerHTML = sections().map(([title, rows]) => `<section class="shortcuts-section">
      <h3 class="shortcuts-section-title">${escapeHtml(title)}</h3>
      ${rows.map(([label, keys, detail]) => `<div class="shortcut-row">
        <span class="shortcut-desc">${escapeHtml(label)}${detail ? `<small>${escapeHtml(detail)}</small>` : ''}</span>
        <span class="shortcut-keys">${keys.map(key => `<kbd class="shortcut-key">${escapeHtml(key)}</kbd>`).join(`<span class="shortcut-or">${escapeHtml(t('app.shortcuts.or'))}</span>`)}</span>
      </div>`).join('')}</section>`).join('');
    filter();
    const previousFocus = document.activeElement;
    show('shortcuts-modal').finally(() => previousFocus?.isConnected && previousFocus.focus());
  };
  document.getElementById('shortcuts-open').addEventListener('click', open);
  document.getElementById('shortcuts-close').addEventListener('click', () => close('shortcuts-modal'));
  input.addEventListener('input', filter);
  modal.addEventListener('click', event => { if (event.target === modal) close('shortcuts-modal'); });
  document.addEventListener('orbital:show-shortcuts', open);
}

export function showShortcutHelp() {
  document.dispatchEvent(new Event('orbital:show-shortcuts'));
}
